import { types, Instance, flow, getRoot } from 'mobx-state-tree';
import api from '../services/api';
// Persistence disabled - all data loaded fresh from workspace endpoint

// User model
const User = types.model('User', {
  id: types.string,
  email: types.string,
  name: types.maybeNull(types.string),
  avatar: types.maybeNull(types.string),
  organizationId: types.maybeNull(types.string),
  role: types.optional(types.string, 'member'),
});

// Organization model  
const Organization = types.model('Organization', {
  id: types.string,
  slug: types.string,
  name: types.string,
  description: types.maybeNull(types.string),
  status: types.optional(types.enumeration(['active', 'inactive']), 'active'),
});

// Stytch Discovery State
const DiscoveryState = types.model('DiscoveryState', {
  intermediateToken: types.maybeNull(types.string),
  email: types.maybeNull(types.string),
  organizations: types.optional(types.array(types.frozen()), []),
  magicLinkSentTo: types.maybeNull(types.string), // Track magic link sent email
});

// Auth store to replace InstantDB auth
const AuthStore = types
  .model('AuthStore', {
    user: types.maybeNull(User),
    organization: types.maybeNull(Organization),
    accessToken: types.maybeNull(types.string),
    refreshToken: types.maybeNull(types.string),
    tokenExpiresAt: types.maybeNull(types.number),
    isAuthenticated: types.optional(types.boolean, false),
    isLoading: types.optional(types.boolean, false),
    error: types.maybeNull(types.string),
    authCheckAttempts: types.optional(types.number, 0),
    lastAuthCheckFailed: types.optional(types.boolean, false),
    lastAuthCheckTime: types.maybeNull(types.number),
    discoveryState: types.optional(DiscoveryState, {}),
    tempPassword: types.maybeNull(types.string), // Temporary password storage for signup flow
  })
  .views(self => ({
    get isReallyAuthenticated() {
      // Check for user presence and successful auth check
      // Tokens are in httpOnly cookies, not in store
      return !!self.user && self.isAuthenticated && !self.lastAuthCheckFailed;
    }
  }))
  .actions(self => {
    
    // Private helper actions for setting auth state
    const setAuthSuccess = (data: any) => {
      console.log('✅ [AuthStore] Auth check successful. Setting auth state.');
      if (data.user) {
        let userName = undefined;
        if (data.user.name) {
          if (typeof data.user.name === 'string') {
            userName = data.user.name;
          } else if (data.user.name.first_name || data.user.name.last_name) {
            userName = `${data.user.name.first_name || ''} ${data.user.name.last_name || ''}`.trim();
          }
        }
        self.user = {
          id: data.user.id || '',
          email: data.user.email || '',
          name: userName,
          avatar: data.user.avatar === null || data.user.avatar === undefined ? undefined : data.user.avatar,
          organizationId: data.user.organizationId === null || data.user.organizationId === undefined ? undefined : data.user.organizationId,
          role: data.user.role || 'member',
        };
      }
      if (data.organization) {
        self.organization = {
          id: data.organization.id || '',
          slug: data.organization.slug || '',
          name: data.organization.name || '',
          description: data.organization.description || undefined,
          status: data.organization.status || 'active',
        };
      }
      self.isAuthenticated = true;
      self.isLoading = false;
      self.error = undefined;
      self.lastAuthCheckFailed = false;
      self.authCheckAttempts = 0; // Reset on success
      (self as any).startTokenRefreshTimer();
    };

    const setAuthFailure = () => {
      console.warn('🚫 [AuthStore] Auth check failed. Clearing auth state.');
      (self as any).clearAuth();
      self.lastAuthCheckFailed = true;
      self.isLoading = false;
    };
    
    return {
    setLoading(loading: boolean) {
      self.isLoading = loading;
    },
    
    setError(error: string | null | undefined) {
      self.error = error || undefined;
    },
    
    setAuth: flow(function* setAuth(authData) {
      // Handle null values from backend
      if (authData.user) {
        // Convert name object to string if needed
        let userName = undefined;
        if (authData.user.name) {
          if (typeof authData.user.name === 'string') {
            userName = authData.user.name;
          } else if (authData.user.name.first_name || authData.user.name.last_name) {
            userName = `${authData.user.name.first_name || ''} ${authData.user.name.last_name || ''}`.trim();
          }
        }
        
        self.user = {
          id: authData.user.id || '',
          email: authData.user.email || '',
          name: userName,
          avatar: authData.user.avatar === null || authData.user.avatar === undefined ? undefined : authData.user.avatar,
          organizationId: authData.user.organizationId === null || authData.user.organizationId === undefined ? undefined : authData.user.organizationId,
          role: authData.user.role || 'member',
        };
      }
      
      if (authData.organization) {
        const orgSlug = authData.organization.slug || '';
        self.organization = {
          id: authData.organization.id || '',
          slug: orgSlug,
          name: authData.organization.name || '',
          description: authData.organization.description || undefined,
          status: authData.organization.status || 'active',
        };
        // org_slug is managed by the backend via httpOnly cookies
        
        // Also hydrate the organization in UserStore so currentOrganization is available
        try {
          const appStore = getRoot(self) as any;
          if (appStore.user && appStore.user.hydrateOrganization) {
            console.log('🔄 [AuthStore.setAuth] Hydrating organization in UserStore');
            appStore.user.hydrateOrganization({
              id: authData.organization.id || '',
              slug: orgSlug,
              name: authData.organization.name || '',
              description: authData.organization.description || undefined,
              status: authData.organization.status || 'active',
            });
          }
        } catch (error) {
          console.warn('[AuthStore.setAuth] Could not hydrate organization in UserStore:', error);
        }
      }
      
      self.accessToken = authData.token || undefined;
      self.isAuthenticated = true;
      self.isLoading = false;
      self.error = undefined;
    }),
    
    clearAuth() {
      self.user = undefined;
      self.organization = undefined;
      // org_slug is managed by the backend via httpOnly cookies
      self.accessToken = undefined;
      self.refreshToken = undefined;
      self.tokenExpiresAt = undefined;
      self.isAuthenticated = false;
      self.isLoading = false;
      self.error = undefined;
      // DON'T set lastAuthCheckFailed = true here - this prevents infinite retry loops
      // self.lastAuthCheckFailed = true;
      
      // Note: Tokens are in httpOnly cookies, cleared by backend on signout
      
      // Note: UserStore will be cleared by AppInitializer on next auth check
    },
    
    // Check JWT session on app load
    checkAuth: flow(function* checkAuth(force: boolean = false) {
      const callId = Math.random().toString(36).substr(2, 9);
      console.log(`[AuthStore.checkAuth] #${callId} Running checkAuth (force: ${force})`);

      if (self.isAuthenticated && !force) {
        console.log(`[AuthStore.checkAuth] #${callId} User is already authenticated. Skipping.`);
        return true;
      }
      console.log(`🔐 [AuthStore.checkAuth] CALL START #${callId}`);
      console.log(`🔐 [AuthStore.checkAuth] Current state:`, {
        isAuthenticated: self.isAuthenticated,
        isLoading: self.isLoading,
        lastAuthCheckFailed: self.lastAuthCheckFailed,
        authCheckAttempts: self.authCheckAttempts,
        hasUser: !!self.user,
        hasOrg: !!self.organization
      });
      
      // Prevent duplicate concurrent calls
      if (self.isLoading) {
        console.log(`❌ [AuthStore.checkAuth] #${callId} Already checking auth, skipping duplicate call`);
        return;
      }
      
      // Prevent infinite retry loops - if auth failed recently, don't retry immediately
      const now = Date.now();
      const timeSinceLastCheck = self.lastAuthCheckTime ? now - self.lastAuthCheckTime : Infinity;
      const cooldownPeriod = 30000; // 30 seconds cooldown
      
      if (self.lastAuthCheckFailed && self.authCheckAttempts >= 3) {
        console.log(`🛑 [AuthStore.checkAuth] #${callId} Too many failed attempts (${self.authCheckAttempts}), stopping retries`);
        return;
      }
      
      if (self.lastAuthCheckFailed && timeSinceLastCheck < cooldownPeriod) {
        console.log(`⏰ [AuthStore.checkAuth] #${callId} Cooldown active - ${Math.round((cooldownPeriod - timeSinceLastCheck) / 1000)}s remaining`);
        return;
      }
      
      console.log(`🏁 [AuthStore.checkAuth] #${callId} Starting auth check... (attempt ${self.authCheckAttempts + 1})`);
      self.isLoading = true;
      self.authCheckAttempts += 1;
      self.lastAuthCheckTime = Date.now();
      
      try {
        console.log(`[AuthStore.checkAuth] #${callId} Sending GET request to /auth/verify...`);
        const response = yield api.get('/auth/verify');
        console.log(`[AuthStore.checkAuth] #${callId} /auth/verify response received`, response.data);
        
        if (response.data && response.data.user) {
          setAuthSuccess(response.data);
          return true;
        } else {
          setAuthFailure();
          return false;
        }
      } catch (error: any) {
        console.error(`[AuthStore.checkAuth] #${callId} Auth check failed:`, error.isAxiosError ? error.message : error);
        setAuthFailure();
        return false;
      }
    }),
    
    // Sign in with Stytch
    signIn: flow(function* signIn(email, password, orgSlug) {
      console.log('🔐 Starting sign in:', { email, orgSlug });
      console.log('🔐 [AuthStore.signIn] Pre-signin state:', {
        isAuthenticated: self.isAuthenticated,
        hasUser: !!self.user,
        hasOrg: !!self.organization,
        hasAccessToken: !!self.accessToken,
        isLoading: self.isLoading
      });
      
      self.isLoading = true;
      self.error = undefined;
      
      try {
        console.log('📡 Sending signin request to backend...');
        console.log('Request body:', { email, password, orgSlug });
        console.log('🔍 Current window location before fetch:', window.location.href);
        
        // Direct fetch call without Promise wrapper
        const response = yield fetch('http://localhost:4011/auth/signin', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include', // Include cookies in request
          body: JSON.stringify({ email, password, orgSlug }),
        });
        
        console.log('📨 Response status:', response.status);
        console.log('📨 Response headers:', [...response.headers.entries()]);
        const result = yield response.json();
        console.log('📦 Response data:', result);
        console.log('📦 [AuthStore.signIn] Full response:', JSON.stringify(result, null, 2));
        
        if (result.success) {
          // Handle null values from backend by converting to undefined for optional fields
          if (result.user) {
            // Convert name object to string if needed
            let userName = undefined;
            if (result.user.name) {
              if (typeof result.user.name === 'string') {
                userName = result.user.name;
              } else if (result.user.name.first_name || result.user.name.last_name) {
                userName = `${result.user.name.first_name || ''} ${result.user.name.last_name || ''}`.trim();
              }
            }
            
            self.user = {
              id: result.user.id || '',
              email: result.user.email || '',
              name: userName,
              avatar: result.user.avatar === null || result.user.avatar === undefined ? undefined : result.user.avatar,
              organizationId: result.user.organizationId === null || result.user.organizationId === undefined ? undefined : result.user.organizationId,
              role: result.user.role || 'member',
            };
          }
          
          if (result.organization) {
            self.organization = {
              id: result.organization.id || '',
              slug: result.organization.slug || '',
              name: result.organization.name || '',
              description: result.organization.description || undefined,
              status: result.organization.status || 'active',
            };
          }
          
          // Note: Tokens are now in httpOnly cookies, not in response
          // Set a placeholder token so isReallyAuthenticated works
          self.accessToken = 'httponly-cookie';
          self.isAuthenticated = true;
          self.lastAuthCheckFailed = false; // Clear any previous auth check failure
          self.authCheckAttempts = 0; // Reset attempts counter
          
          console.log('✅ [AuthStore.signIn] Setting auth state:', {
            isAuthenticated: self.isAuthenticated,
            accessToken: self.accessToken,
            user: self.user,
            organization: self.organization,
            lastAuthCheckFailed: self.lastAuthCheckFailed,
            isReallyAuthenticated: !!self.accessToken && !self.lastAuthCheckFailed
          });
          
          // Start the token refresh timer
          (self as any).startTokenRefreshTimer();
          
          console.log('✅ Signed in successfully, user:', self.user);
          console.log('✅ [AuthStore.signIn] Post-signin state:', {
            isAuthenticated: self.isAuthenticated,
            hasUser: !!self.user,
            hasOrg: !!self.organization,
            hasAccessToken: !!self.accessToken
          });
          return { success: true };
        } else {
          self.error = result.message || 'Sign in failed';
          console.error('❌ Sign in failed:', result.message);
          return { success: false, error: result.message };
        }
      } catch (error) {
        self.error = 'Network error during sign in';
        console.error('❌ Sign in error:', error);
        return { success: false, error: 'Network error' };
      } finally {
        self.isLoading = false;
        console.log('🔓 Sign in complete, loading:', self.isLoading);
      }
    }),
    
    // Refresh access token
    refreshAccessToken: flow(function* refreshAccessToken() {
      const refreshToken = self.refreshToken; // Refresh token is in httpOnly cookies
      
      if (!refreshToken) {
        console.warn('[AuthStore] No refresh token available, clearing auth');
        (self as any).clearAuth();
        return false;
      }
      
      try {
        console.log('[AuthStore] Attempting to refresh access token...');
        const response = yield fetch('http://localhost:4011/auth/refresh', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
        
        const result = yield response.json();
        
        if (result.success && result.accessToken) {
          self.accessToken = result.accessToken;
          // If a new refresh token is provided, update it
          if (result.refreshToken) {
            self.refreshToken = result.refreshToken;
            // Refresh token is managed by httpOnly cookies
          }
          self.tokenExpiresAt = Date.now() + (result.expiresIn * 1000);
          
          // Tokens are managed by httpOnly cookies, not localStorage
          
          console.log('✅ Token refreshed successfully');
          return true;
        } else {
          console.warn('[AuthStore] Token refresh failed:', result.message || 'Unknown error');
          (self as any).clearAuth();
          return false;
        }
      } catch (error) {
        console.error('❌ Token refresh failed:', error);
        (self as any).clearAuth();
        return false;
      }
    }),
    
    signOut: flow(function* signOut() {
      try {
        // Call backend to clear httpOnly cookies
        const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:4011';
        yield fetch(`${baseUrl}/auth/signout`, {
          method: 'POST',
          credentials: 'include', // Include cookies so backend can clear them
        });
        console.log('🍪 Backend cookies cleared');
      } catch (error) {
        console.error('Sign out request failed:', error);
        // Continue with logout even if backend call fails
      }
      
      // Clear localStorage items
      localStorage.removeItem('auth_flow');
      localStorage.removeItem('auth_email');
      localStorage.removeItem('selected_org');
      localStorage.removeItem('user_email');
      console.log('🧹 localStorage cleared');
      
      // Clear MobX auth state
      (self as any).clearAuth();
      console.log('✅ Signed out - redirecting to login');
      
      // Redirect to login page
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    }),
    
    // Start automatic token refresh
    startTokenRefreshTimer() {
      // Clear any existing timer
      (self as any).stopTokenRefreshTimer();
      
      // Check every hour if token needs refresh (we have 265-day sessions now!)
      const interval = setInterval(() => {
        if ((self as any).shouldRefreshToken && self.isAuthenticated) {
          console.log('[AuthStore] Token needs refresh, attempting...');
          (self as any).refreshAccessToken().then((success: boolean) => {
            if (!success) {
              console.warn('[AuthStore] Token refresh failed, user may need to sign in again');
            }
          }).catch((error) => {
            console.error('[AuthStore] Token refresh error:', error);
          });
        }
      }, 60 * 60 * 1000); // Check every hour (sessions last 265 days now!)
      
      // Store interval ID for cleanup
      (self as any).__refreshInterval = interval;
      console.log('[AuthStore] Token refresh timer started');
    },
    
    stopTokenRefreshTimer() {
      if ((self as any).__refreshInterval) {
        clearInterval((self as any).__refreshInterval);
        (self as any).__refreshInterval = null;
      }
    },
    
    // === STYTCH DISCOVERY FLOW ACTIONS ===
    
    clearMagicLinkSent() {
      self.discoveryState.magicLinkSentTo = null;
    },

    setPasswordSignupCredentials(email: string, password: string) {
      self.discoveryState.email = email;
      self.tempPassword = password;
    },

    sendDiscoveryMagicLink: flow(function* (email: string) {
      console.log('🔐 [AuthStore] Sending discovery magic link to:', email);
      self.isLoading = true;
      self.error = null;
      
      try {
        const response = yield api.post('/auth/signup', { email });
        
        console.log('📦 [AuthStore] Full response:', response);
        console.log('📦 [AuthStore] Response data:', response.data);
        console.log('📦 [AuthStore] Response status:', response.status);
        console.log('📦 [AuthStore] Success value:', response.data?.success);
        
        if (response.data?.success) {
          console.log('✅ [AuthStore] Magic link sent successfully, setting magicLinkSentTo:', email);
          self.discoveryState.magicLinkSentTo = email; // Store in MobX
          return { success: true, email: response.data.email };
        }
        
        console.warn('⚠️ [AuthStore] Response data does not indicate success');
        return { success: false, message: response.data?.message || 'Failed to send magic link' };
      } catch (error: any) {
        console.error('❌ [AuthStore] Send magic link failed:', error);
        console.error('❌ [AuthStore] Error response:', error.response);
        self.error = error.message || 'Failed to send magic link';
        return { success: false, message: self.error };
      } finally {
        self.isLoading = false;
      }
    }),

    authenticatePassword: flow(function* (email: string, password: string) {
      console.log('🔐 [AuthStore] Authenticating with password for:', email);
      self.isLoading = true;
      self.error = null;
      
      try {
        const response = yield api.post('/auth/password/authenticate', { email, password });
        
        console.log('📦 [AuthStore] Password auth response:', response.data);
        
        if (response.data?.success === false) {
          console.error('❌ [AuthStore] Password auth failed:', response.data.message);
          return { success: false, message: response.data.message };
        }
        
        // Store discovery state (same as magic link flow)
        if (response.data?.intermediateToken) {
          self.discoveryState.intermediateToken = response.data.intermediateToken;
          self.discoveryState.email = email;
        }
        
        if (response.data?.organizations) {
          self.discoveryState.organizations = response.data.organizations;
        }
        
        console.log('✅ [AuthStore] Password auth successful');
        return response.data;
      } catch (error: any) {
        console.error('❌ [AuthStore] Password auth error:', error);
        self.error = error.response?.data?.message || error.message || 'Authentication failed';
        return { success: false, message: self.error };
      } finally {
        self.isLoading = false;
      }
    }),

    signupWithPassword: flow(function* (email: string, password: string, orgName: string, orgSlug: string) {
      console.log('🔐 [AuthStore] Password signup:', { email, orgName, orgSlug });
      self.isLoading = true;
      self.error = null;
      
      try {
        const response = yield api.post('/auth/signup-with-password', {
          email,
          password,
          orgName,
          orgSlug,
        });
        
        console.log('📦 [AuthStore] Signup response:', response.data);
        
        if (response.data?.success) {
          console.log('✅ [AuthStore] Password signup successful - email verification sent');
          
          // Store pending password for after email verification
          if (response.data.pendingPassword) {
            self.tempPassword = response.data.pendingPassword;
          }
          
          // Store email for "check your email" screen
          self.discoveryState.email = response.data.email;
          
          return {
            success: true,
            needsEmailVerification: true,
            message: response.data.message || 'Check your email to verify and set password!',
            orgSlug: response.data.organization?.slug,
          };
        } else {
          console.error('❌ [AuthStore] Signup failed:', response.data.message);
          return { success: false, message: response.data.message };
        }
      } catch (error: any) {
        console.error('❌ [AuthStore] Signup error:', error);
        self.error = error.response?.data?.message || error.message || 'Signup failed';
        return { success: false, message: self.error };
      } finally {
        self.isLoading = false;
      }
    }),

    setPasswordWithToken: flow(function* (token: string, password: string) {
      console.log('🔐 [AuthStore] Setting password with email verification token');
      console.log('   Token:', token.substring(0, 20) + '...');
      self.isLoading = true;
      self.error = null;
      
      try {
        const response = yield api.post('/auth/set-password', { token, password });
        
        console.log('📦 [AuthStore] Set password response:', response.data);
        
        if (response.data?.success) {
          const userData = response.data.user;
          const orgData = response.data.organization;
          
          console.log('✅ [AuthStore] Password set successfully, user:', userData.email);
          console.log('✅ [AuthStore] Organization:', orgData.slug);
          
          self.user = {
            id: userData.id,
            email: userData.email,
            name: null,
            avatar: null,
            organizationId: orgData.id,
            role: 'owner',
          };
          
          self.organization = {
            id: orgData.id,
            slug: orgData.slug,
            name: orgData.name,
            description: null,
            status: 'active',
          };
          
          // Mark as authenticated (cookies are set by backend as httpOnly)
          self.isAuthenticated = true;
          self.lastAuthCheckFailed = false;
          self.tempPassword = null; // Clear temp password
          
          console.log('✅ [AuthStore] Auth state updated, isAuthenticated:', self.isAuthenticated);
          console.log('✅ [AuthStore] User will be redirected to:', `/${orgData.slug}/mindmap`);
          
          // Initialize the app workspace after successful auth
          const appStore = getRoot(self) as any;
          if (appStore && appStore.initializeApp) {
            console.log('🔄 [AuthStore] Initializing workspace for:', orgData.slug);
            yield appStore.initializeApp(orgData.slug);
            console.log('✅ [AuthStore] Workspace initialized');
          }
          
          return { success: true, orgSlug: orgData.slug };
        } else {
          console.error('❌ [AuthStore] Set password failed:', response.data.message);
          return { success: false, message: response.data.message };
        }
      } catch (error: any) {
        console.error('❌ [AuthStore] Set password error:', error);
        console.error('❌ [AuthStore] Error response:', error.response?.data);
        self.error = error.response?.data?.message || error.message || 'Failed to set password';
        return { success: false, message: self.error };
      } finally {
        self.isLoading = false;
      }
    }),
    
    authenticateDiscoveryToken: flow(function* (token: string) {
      console.log('🔐 [AuthStore] Authenticating discovery token');
      self.isLoading = true;
      self.error = null;
      
      try {
        const response = yield api.post('/auth/discovery/authenticate', { token });
        const data = response.data;
        
        console.log('📦 [AuthStore] Discovery full response:', JSON.stringify(data, null, 2));
        console.log('📦 [AuthStore] Discovery type:', data.type);
        
        // Store intermediate token and email if present
        if (data.intermediateSessionToken) {
          self.discoveryState.intermediateToken = data.intermediateSessionToken;
        }
        if (data.email) {
          self.discoveryState.email = data.email;
        }
        
        if (data.type === 'create_organization') {
          // New user - needs to create org
          console.log('✅ [AuthStore] New user - needs org creation');
          return {
            needsOrgCreation: true,
            email: data.email,
          };
        } else if (data.type === 'select_organization') {
          // Existing user - has orgs to choose from
          console.log('✅ [AuthStore] Existing user - has orgs:', data.organizations?.length);
          self.discoveryState.organizations = data.organizations || [];
          return {
            needsOrgSelection: true,
            organizations: data.organizations,
          };
        } else if (data.type === 'expired') {
          console.error('❌ [AuthStore] Token expired');
          throw new Error(data.message);
        } else if (data.success === false) {
          console.error('❌ [AuthStore] Backend returned error:', data.message);
          throw new Error(data.message || 'Authentication failed');
        }
        
        console.error('❌ [AuthStore] Unexpected response type:', data.type, 'Full data:', data);
        return { success: false, message: 'Unexpected response from server' };
      } catch (error: any) {
        console.error('❌ [AuthStore] Discovery auth failed:', error);
        self.error = error.message || 'Authentication failed';
        return { success: false, message: self.error };
      } finally {
        self.isLoading = false;
      }
    }),
    
    createOrganization: flow(function* (orgName: string, orgSlug: string) {
      console.log('🏢 [AuthStore] Creating organization:', orgName, orgSlug);
      // Get email from localStorage (set by AuthCallback)
      const email = localStorage.getItem('auth_email');
      console.log('📧 [AuthStore] Creator email from localStorage:', email);
      self.isLoading = true;
      self.error = null;
      
      try {
        // Backend reads intermediate token from httpOnly cookie
        const response = yield api.post('/auth/create-organization', {
          organizationName: orgName,
          organizationSlug: orgSlug,
          email, // Send creator's email for domain-based JIT provisioning
        });
        
        const data = response.data;
        
        if (data.success) {
          // Clear auth flow state - we're fully logged in now!
          localStorage.removeItem('auth_flow');
          localStorage.removeItem('auth_email');
          console.log('🧹 [AuthStore] Cleared auth flow state from localStorage');
          
          // Update auth state
          self.user = {
            id: data.user.id,
            email: data.user.email,
            name: data.user.name || null,
            avatar: data.user.avatar || null,
            organizationId: data.organization.id,
            role: data.user.role || 'owner',
          };
          
          self.organization = {
            id: data.organization.id,
            slug: data.organization.slug,
            name: data.organization.name,
            description: null,
            status: 'active',
          };
          
          self.isAuthenticated = true;
          self.lastAuthCheckFailed = false;
          self.discoveryState.intermediateToken = null; // Clear token
          
          console.log('✅ [AuthStore] Organization created successfully');
          
          // Initialize workspace data
          const appStore = getRoot(self) as any;
          if (appStore && appStore.initializeApp) {
            console.log('🔄 [AuthStore] Initializing workspace...');
            yield appStore.initializeApp(data.organization.slug);
            console.log('✅ [AuthStore] Workspace initialized');
          } else {
            console.error('❌ [AuthStore] Cannot find appStore or initializeApp method');
          }
          
          return { success: true, orgSlug: data.organization.slug };
        }
        
        return { success: false, message: data.message };
      } catch (error: any) {
        console.error('❌ [AuthStore] Create org failed:', error);
        self.error = error.message || 'Failed to create organization';
        return { success: false, message: self.error };
      } finally {
        self.isLoading = false;
      }
    }),
    
    selectOrganization: flow(function* (stytchOrgId: string) {
      console.log('🏢 [AuthStore] Selecting organization:', stytchOrgId);
      self.isLoading = true;
      self.error = null;
      
      try {
        // Backend reads intermediate token from httpOnly cookie
        const response = yield api.post('/auth/select-organization', {
          stytchOrganizationId: stytchOrgId,
        });
        
        const data = response.data;
        
        if (data.success) {
          // Update auth state
          self.user = {
            id: data.user.id,
            email: data.user.email,
            name: data.user.name || null,
            avatar: data.user.avatar || null,
            organizationId: data.organization.id,
            role: data.user.role || 'member',
          };
          
          self.organization = {
            id: data.organization.id,
            slug: data.organization.slug,
            name: data.organization.name,
            description: null,
            status: 'active',
          };
          
          self.isAuthenticated = true;
          self.lastAuthCheckFailed = false;
          self.discoveryState.intermediateToken = null; // Clear token
          
          // Clear auth flow state - we're fully logged in now!
          localStorage.removeItem('auth_flow');
          localStorage.removeItem('auth_email');
          console.log('🧹 [AuthStore] Cleared auth flow state from localStorage');
          
          console.log('✅ [AuthStore] Selected organization successfully');
          
          const appStore = getRoot(self) as any;
          if (appStore && appStore.initializeApp) {
            console.log('🔄 [AuthStore] Initializing workspace...');
            yield appStore.initializeApp(data.organization.slug);
            console.log('✅ [AuthStore] Workspace initialized');
          } else {
            console.error('❌ [AuthStore] Cannot find appStore or initializeApp method');
          }
          
          return { success: true, orgSlug: data.organization.slug };
        }
        
        return { success: false, message: data.message };
      } catch (error: any) {
        console.error('❌ [AuthStore] Select org failed:', error);
        self.error = error.message || 'Failed to select organization';
        return { success: false, message: self.error };
      } finally {
        self.isLoading = false;
      }
    }),
    
    acceptInvite: flow(function* (token: string) {
      console.log('🎉 [AuthStore] Accepting invite');
      self.isLoading = true;
      self.error = null;
      
      try {
        const response = yield api.post('/auth/accept-invite', { token });
        const data = response.data;
        
        if (data.success) {
          // Update auth state
          self.user = {
            id: data.user.id,
            email: data.user.email,
            name: data.user.name || null,
            avatar: data.user.avatar || null,
            organizationId: data.organization.id,
            role: 'member',
          };
          
          self.organization = {
            id: data.organization.id,
            slug: data.organization.slug,
            name: data.organization.name,
            description: null,
            status: 'active',
          };
          
          self.isAuthenticated = true;
          self.lastAuthCheckFailed = false;
          
          console.log('✅ [AuthStore] Invite accepted successfully');
          
          // Initialize workspace data
          const appStore = getRoot(self) as any;
          if (appStore && appStore.initializeApp) {
            console.log('🔄 [AuthStore] Initializing workspace...');
            yield appStore.initializeApp(data.organization.slug);
            console.log('✅ [AuthStore] Workspace initialized');
          } else {
            console.error('❌ [AuthStore] Cannot find appStore or initializeApp method');
          }
          
          return { success: true, orgSlug: data.organization.slug };
        }
        
        return { success: false, message: data.message };
      } catch (error: any) {
        console.error('❌ [AuthStore] Accept invite failed:', error);
        self.error = error.message || 'Failed to accept invite';
        return { success: false, message: self.error };
      } finally {
        self.isLoading = false;
      }
    }),
  }
})
.views(self => ({
    get currentUser() {
      return self.user;
    },
    
    get currentOrg() {
      return self.organization;
    },
    
    get isSignedIn() {
      return self.isAuthenticated && !!self.user;
    },
    
    get shouldRefreshToken() {
      if (!self.tokenExpiresAt) return false;
      // Refresh if token expires in less than 1 day (we have 265-day sessions!)
      return self.tokenExpiresAt - Date.now() < 24 * 60 * 60 * 1000;
    },
  }));

export type IAuthStore = Instance<typeof AuthStore>;
export { AuthStore };
export type AuthStoreType = Instance<typeof AuthStore>;
